<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <h2>
          <v-icon class="mr-2">
            mdi-point-of-sale
          </v-icon>
          請求管理
        </h2>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-expansion-panels accordion>
          <v-expansion-panel>
            <v-expansion-panel-header class="panel-header-color panel-selectable">
              <strong>
                <v-icon class="panel-header-color">
                  mdi-magnify
                </v-icon>
                &nbsp;条件を指定して検索
              </strong>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card
                flat
                >
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="4">
                      <cs-date-picker
                        :date.sync="conditions.billing_month"
                        type="month"
                        @dateChangedCallback="billingMonthChangedCallback"
                      >
                        <template v-slot="{ on, attrs }">
                          <v-text-field
                            v-model="conditions.billing_month"
                            v-bind="attrs"
                            v-on="on"
                            append-icon="mdi-calendar"
                            clearable
                            dense
                            hide-details="auto"
                            label="請求月度"
                            outlined
                            rounded
                            persistent-hint
                            persistent-placeholder
                            readonly
                          />
                        </template>
                      </cs-date-picker>
                    </v-col>

                    <v-col cols="12" md="4">
                      <infinite-billing-auto-complete
                        :billing-id="conditions.billing_address_id"
                        rounded
                        classes="search-font"
                        label="請求先"
                        @update:values="billingAddressChangedCallback"
                      >
                        <template v-slot:append-outer>
                          <choose-billing-address-button
                            :small-button="true"
                            :self-tenant="true"
                            @choseBillingAddressCallback="choseBillingAddressCallback"
                          />
                        </template>
                      </infinite-billing-auto-complete>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-select
                        v-model="conditions.close_day_type"
                        :items="closingDateTypes"
                        clearable
                        dense
                        hide-details="auto"
                        label="締め日"
                        outlined
                        persistent-placeholder
                        rounded
                        style="font-size: 80%"
                        @change="searchButtonClickEvent"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="conditions.status"
                        :items="billingStatuses"
                        clearable
                        dense
                        hide-details="auto"
                        label="ステータス"
                        outlined
                        persistent-placeholder
                        rounded
                        style="font-size: 80%"
                        @change="searchButtonClickEvent"
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select
                        v-model="conditions.bill_payment_type"
                        :items="billingPaymentTypes"
                        clearable
                        dense
                        hide-details="auto"
                        label="請求支払区分"
                        outlined
                        persistent-placeholder
                        rounded
                        style="font-size: 80%"
                        @change="searchButtonClickEvent"
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select
                        v-model="conditions.bill_output_setting"
                        :items="billingOutputSettings"
                        clearable
                        dense
                        hide-details="auto"
                        label="請求書発行状況"
                        outlined
                        persistent-placeholder
                        rounded
                        style="font-size: 80%"
                        @change="searchButtonClickEvent"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    @click="searchButtonClickEvent"
                  >
                    <v-icon>
                      mdi-magnify
                    </v-icon>
                    &nbsp;検索
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="text-right">
        <v-btn
          class="info"
          @click="allBillingConfirm"
        >
          一括仮締め
        </v-btn>&emsp;
        <v-btn
          class="info"
          @click="invoiceOutputDialog"
        >
          請求書出力
        </v-btn>&emsp;
        <v-btn
          class="info"
          @click="closingProcessDialog"
        >
          本締め処理
        </v-btn>&emsp;
        <v-btn
          color="primary"
          @click="billingProcessDialog"
        >
          請求計上
        </v-btn>&emsp;
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <custom-pagination
          :page-number="page"
          :per-page="perPage"
          :pagination="pagination"
          :total-visible="totalVisible"
          @pageChanged="changePage"
          @perPageChanged="changePerPage"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card flat class="rounded-card">
          <v-data-table
            :headers="tableHeaders"
            :items="billings"
            :items-per-page="-1"
            hide-default-footer
            disable-sort
          >
            <!-- ステータス -->
            <template v-slot:[`item.status`]="{ item }">
              <v-chip
                x-small
                label
                dark
                class="stretchedChip"
                :color="item.status | status_chip_request_color"
              >
                {{ item.status | status_to_label_request }}
              </v-chip>
            </template>
            <!-- /ステータス -->
            <!-- 確定処理 -->
            <template v-slot:[`item.process`]="{ item }">
              <v-btn
                v-if="['billing_unsettled'].includes(item.status)"
                text
                color="primary"
                small
                @click="temporaryFasteningButtonClickEvent(item)"
              >
                仮締めする
              </v-btn>
              <v-btn
                v-else-if="['billing_confirm'].includes(item.status)"
                text
                color="primary"
                small
                @click="unfasteningButtonClickEvent(item)"
              >
                未締に戻す
              </v-btn>
            </template>
            <!-- /確定処理 -->
            <template v-slot:[`item.billing_name`]="{ item }">
              <div
                class="line-clamp one-clamp"
              >
                {{ item.billing_name }}
              </div>
            </template>
            <template v-slot:[`item.billing_month`]="{ item }">
              <div>
                {{ item.billing_month }}
              </div>
              {{ item.billing_term }}
            </template>
            <template v-slot:[`item.billing_payment_type`]="{item}">
              <div>
                <v-chip
                  x-small
                  label
                  dark
                  :color="item.billing_payment_type | billing_payment_type_color"
                >
                  {{ item.billing_payment_type | billing_payment_type_label }}
                </v-chip>
              </div>
              <span class="font-weight-black">
                {{ parseFloat(item.plan_billing_amount) | comma_format }}
              </span>
            </template>
            <template v-slot:[`item.billing_change_at`]="{ item }">
              <div>
                {{ item.billing_change_at | unix_datetime_format }}
              </div>
              <span v-if="item.billing_warning" style="color:#FF0000">
                ●
              </span>
              <span v-if="item.billing_output_at">
                {{ item.billing_output_at | unix_datetime_format }}
              </span>
              <span v-else>
                -
              </span>
            </template>
            <template v-slot:[`item.closing_date`]="{item}">
              {{ item.closing_date | date_format }}
            </template>

            <!-- 詳細 -->
            <template v-slot:[`item.editable`]="{ item }">
              <v-menu
                bottom
                left
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                {{ item.name }}
                <v-list dense>
                  <v-list-item
                    link
                    @click="showButtonClickEvent(item)"
                  >
                    <v-list-item-title>
                      詳細を参照
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <!-- /詳細 -->
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- ページャ -->
    <v-row>
      <v-col>
        <custom-pagination
          :page-number="page"
          :per-page="perPage"
          :pagination="pagination"
          :total-visible="totalVisible"
          @pageChanged="changePage"
          @perPageChanged="changePerPage"
        />
      </v-col>
    </v-row>
    <!-- /ページャ -->
    <closing-process-dialog
      :enable-dialog="enableClosingProcessDialog"
      @registerCallback="closingProcessRegisterCallback"
      @cancelCallback="closingProcessCancelCallback"
    />
    <billing-process-dialog
      :enable-dialog="enableBillingProcessDialog"
      @registerCallback="billingProcessRegisterCallback"
      @cancelCallback="billingProcessCancelCallback"
    />
    <invoice-output-dialog
      :enable-dialog="enableInvoiceOutputDialog"
      @registerCallback="invoiceOutputRegisterCallback"
      @cancelCallback="invoiceOutputCancelCallback"
    />
    <confirm ref="confirm" />
  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import Confirm from '@/components/Confirm'
import ClosingProcessDialog from './dialogs/_ClosingProcessDialog'
import BillingProcessDialog from './dialogs/_BillingProcessDialog'
import InvoiceOutputDialog from './dialogs/_InvoiceOutputDialog'
import ChooseBillingAddressButton from "@/driver/components/ChooseBillingAddressButton";
import CustomPagination from "@/driver/components/_CustomPagination.vue";
import InfiniteBillingAutoComplete from "@/driver/components/InfiniteBillingAutoComplete.vue";

export default {
  name: 'DriverSalesRequests',
  components: {
    InfiniteBillingAutoComplete,
    CustomPagination,
    Confirm,
    ChooseBillingAddressButton,
    ClosingProcessDialog,
    BillingProcessDialog,
    InvoiceOutputDialog
  },
  mixins: [],
  props: {},
  data() {
    return {
      page: 1,
      perPage: 50,
      totalVisible: 7,
      showTargetDatePicker: false,
      conditions: {
        /* 請求年月 */
        billing_month: this.$dayjs().format("YYYY-MM"),
        /* 請求先 */
        billing_address_id: null,
        /* 締め日 */
        close_day_type: null,
        /* ステータス */
        status: null,
        /* 請求区分 */
        bill_payment_type: null,
        /* 請求書発行状況 */
        bill_output_setting: null,
      },
      enableClosingProcessDialog: false,
      enableBillingProcessDialog: false,
      enableInvoiceOutputDialog: false,
      rowSelected: [],
      billings: [],
      /** 締め日 */
      closingDateTypes: [
        { value: 'closing_5th_day', text: '5日' },
        { value: 'closing_10th_day', text: '10日' },
        { value: 'closing_15th_day', text: '15日' },
        { value: 'closing_20th_day', text: '20日' },
        { value: 'closing_25th_day', text: '25日' },
        { value: 'closing_last_day', text: '末日' },
      ],
      /**  ステータス */
      billingStatuses: [
        { value: 'billing_closed', text: '本締め' },
        { value: 'billing_confirm', text: '仮締め' },
        { value: 'billing_unsettled', text: '未締' },
      ],
      billingPaymentTypes: [
        { value: 'billing_type', text: '請求' },
        { value: 'payment_type', text: '支払' },
      ],
      billingOutputSettings: [
        { value: 'output', text: '発行済' },
        { value: 'non_output', text: '未発行' },
      ],
      tableHeaders: [
        { text: 'ステータス', value: 'status', width: '7%'},
        { text: '仮締め処理', value: 'process', align: 'center', width: '12%' },
        { text: '請求先', value: 'billing_name', width: '20%' },
        { text: '締日/ｻｲﾄ/入金', value: 'billing_info', width: '12%' },
        { text: '請求月度\r\n請求対象期間', value: 'billing_month', width: '12%' },
        { text: '請求支払区分\r\n請求予定金額', value: 'billing_payment_type',align: 'center', width: '12%' },
        { text: '請求額変更日時\r\n請求書出力日時', value: 'billing_change_at', width: '10%' },
        { text: '締め日', value: 'closing_date', width: '10%' },
        { text: '編集', value: 'editable', align: 'end', width: '5%' },
      ],
    }
  },
  computed: {
    ...mapState('DriverSalesRequest', {
      pagination: 'pagination',
      searchCondition: 'condition',
    }),
  },
  watch: {},
  created() {
    if (this.$route.params.resetCondition) {
      this.resetState()
      this.searchCondition.billing_month = this.$dayjs().format("YYYY-MM")
    }
    if (this.$route.params.resetPagination) {
      this.resetPagination()
    }
    Object.assign(this.conditions, this.searchCondition);
    this.page = this.pagination.current_page
    this.perPage = this.searchCondition.limit
    this.searchRequests()
  },
  methods: {
    ...mapActions('DriverSalesRequest', {
      fetchRequests: 'index',
      updateToStatus: 'update',
      resetState: 'resetState',
      resetPagination: 'resetPagination',
    }),
    ...mapActions('DriverSalesRequestBillingConfirm', {
      updateBillingConfirmStatus: 'update'
    }),
    /**
     * 仮締めボタン クリックイベント
     * @param item
     */
    temporaryFasteningButtonClickEvent (item) {
      let modalOption = { title: '更新', message: '仮締め登録を行います。よろしいですか？', options: { titleColor: 'blue', width: 350, } }
      this.$refs.confirm.open(modalOption).then((confirm) => {
        this.$refs.confirm.close()
        if (confirm) {
          this.updateToStatus({ id: item.id, params: {status: 'billing_confirm' }}).then(() => {
            this.searchRequests()
          })
        }
      })
    },
    /**
     * 未締めボタン クリックイベント
     * @param item
     */
    unfasteningButtonClickEvent (item) {
      let modalOption = { title: '更新', message: '未締め登録を行います。よろしいですか？', options: { titleColor: 'blue', width: 350, } }
      this.$refs.confirm.open(modalOption).then((confirm) => {
        this.$refs.confirm.close()
        if (confirm) {
          this.updateToStatus({id: item.id, params: {status: 'billing_unsettled' } }).then(() => {
            this.searchRequests()
          })
        }
      })
    },
    /**
     * 一括仮締めボタン クリックイベント
     */
    allBillingConfirm () {
      let modalOption = { title: '更新', message: '一括仮締めを行います。よろしいですか？', options: { titleColor: 'blue', width: 350, } }
      this.$refs.confirm.open(modalOption).then((confirm) => {
        this.$refs.confirm.close()
        if (confirm) {
          let params = this.buildParam('json')
            this.updateBillingConfirmStatus({params: params}).then(() => {
            this.searchRequests()
          })
        }
      })
    },
    billingAddressChangedCallback (billing_id) {
      let changed = false;
      if (this.conditions.billing_address_id !== billing_id) {
        changed = true;
      }
      this.conditions.billing_address_id = billing_id;
      if (changed) {
        this.searchButtonClickEvent();
      }
    },
    /**
     * 請求先選択ボタンからのダイアログのコールバック
     * @param item
     */
    choseBillingAddressCallback (item) {
      this.conditions.billing_address_id = item.id;
      this.searchButtonClickEvent();
    },
    /**
     * ページボタン クリックイベント
     */
    changePage(page) {
      this.page = page;
      this.searchRequests()
    },
    changePerPage (perPage) {
      this.perPage = perPage
      this.page = 1
      this.searchRequests()
    },
    /**
     * 請求月度変更　コールバック
     */
    billingMonthChangedCallback (value) {
      this.conditions.billing_month = value;
      this.searchButtonClickEvent();
    },
    /**
     * 検索ボタン クリックイベント
     */
    searchButtonClickEvent () {
      this.page = 1
      this.searchRequests()
    },
    /**
     * 締め処理ボタン クリックイベント
     */
    closingProcessDialog () {
      this.enableClosingProcessDialog = true
    },
    /**
     * 請求計上ボタン クリックイベント
     */
    billingProcessDialog () {
      this.enableBillingProcessDialog = true
    },
    /**
     * 請求書出力ボタン クリックイベント
     */
    invoiceOutputDialog () {
      this.enableInvoiceOutputDialog = true
    },
    /**
     * 締め処理 登録ボタンコールバック
     */
    closingProcessRegisterCallback () {
      this.closingProcessCancelCallback()
      this.searchRequests()
    },
    /**
     * 請求計上 登録ボタンコールバック
     */
    billingProcessRegisterCallback () {
      this.enableBillingProcessDialog = false
      this.searchRequests()
    },
    /**
     * 請求書出力 登録ボタンコールバック
     */
    invoiceOutputRegisterCallback () {
      this.invoiceOutputCancelCallback()
      this.searchRequests()
    },
    /**
     * 締め処理 キャンセルボタンコールバック
     */
    closingProcessCancelCallback () {
      this.enableClosingProcessDialog = false
    },
    /**
     * 請求計上 キャンセルボタンコールバック
     */
    billingProcessCancelCallback () {
      this.enableBillingProcessDialog = false
    },
    /**
     * 請求書出力 キャンセルボタンコールバック
     */
    invoiceOutputCancelCallback () {
      this.enableInvoiceOutputDialog = false
    },
    showButtonClickEvent (item) {
      this.$router.push({name: 'DriverSalesRequest', params: { id: item.id }})
    },

    searchRequests () {
      let params = this.buildParam('json')
      this.fetchRequests({ conditions: params }).then((content) => {
        this.billings = content.billing_transactions
      })
    },
    buildParam (format) {
      return {
        ...this.conditions,
        page: this.page,
        limit: this.perPage,
        format: format
      }
    },
  }
}
</script>

<style scoped lang="scss">


</style>
